/* eslint-disable no-template-curly-in-string */
/* eslint-disable consistent-return */
import React from 'react';
import { object, string, ref } from 'yup';
import moment from 'moment';

import FormGroup from '~/commons/components/FormGroup.jsx';
import FormCheckGroup from '~/commons/components/FormCheckGroup.jsx';
import { env } from '~/commons/config';
import { cloneDeep } from 'lodash';
import FormResource from './FormResource.jsx';

const defaults = {
  active: true,
  email: '',
  name: { full: { en: '', zhHant: '' } },
  title: '',
  mobile: {
    number: '',
    code: '',
  },
  username: '',
  userId: '',
  salesRepId: '',
  salesGroupIds: '',
  orgIds: '',
  showroom: '',
  password: '',
  confirmPassword: '',
  roles: ['department'],
  department: '',
  lastLogin: '',
};

const password = string().matches(
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=[a-zA-Z0-9!@#$^*]*$)(?=.{8,20}$)/,
  'Password must contain 8-20 characters, one uppercase, one lowercase, one number',
);
const english = /^[a-zA-Z'"\-,\s.]*$/;
const nameMsg = '${path} Allow max 60 A-Z , a-z, apostrophe, hyphen, full-stop, space';
const maxMsg = '${path} in max ${max} a/n';
const minMsg = '${path} in min ${min} a/n';
const requMsg = '${path} is a mandatory field';

const schema = (department) => object({
  name: object({
    full: object({
      en: string()
        .label('Name (English)')
        .max(60, nameMsg)
        .matches(english, nameMsg)
        .required(requMsg),
    }),
  }),
  username: string().min(6, minMsg).max(30, maxMsg).alphaNumeric()
    .required(),
  userId: string().label('Oracle User ID').min(6, minMsg).max(30, maxMsg)
    .alphaNumeric(),
  salesRepId: string().max(30, maxMsg).alphaNumeric(),
  showroom: string(),
  orgIds: string().matches(/^(?:\d+,?)+$/, 'Invalid Organization ID Format'),
  mobile: object({
    number: string(),
    code: string(),
  }),
  email: string().email().max(256, maxMsg),
  ...(!department
    ? {
      password: password.required(requMsg),
    }
    : { password }),
  confirmPassword: string().when('password', {
    is: (p) => p,
    then: (s) => s.required(requMsg).oneOf([ref('password')], 'Password does not match'),
  }),
  salesGroupIds: string().matches(/^(?:\d+,?)+$/, 'Invalid sales group IDs'),
});

const notAdmin = !env?.user?.roles?.includes('admin');

const FormDepartment = ({ department, showrooms = [], departments = [] }) => (
  <FormResource
    {...{
      defaults,
      schema: schema(department),
      stored: department
        ? {
          ...department,
          showroom: department.showroom ?? '',
          salesGroupIds: department.salesGroupIds.join(','),
          lastLogin: department.lastLogin ? moment(department.lastLogin).format(env.f.dt) : '',
          orgIds: (department?.orgIds || []).join(','),
        }
        : undefined,
      api: '/api/core/users/departments',
      onSubmit: (x) => {
        const v = cloneDeep(x);
        delete v.lastLogin;
        v.password = v.password ? v.password : undefined;
        v.showroom = v.showroom ? v.showroom : null;
        v.department = v.department ? v.department : undefined;
        ['orgIds', 'salesGroupIds'].forEach((k) => {
          v[k] = v[k]
            ?.replace(/\s/, '')
            .split(',')
            .map((w) => parseInt(w, 10))
            .filter(Boolean);
        });
        return v;
      },
    }}>
    {({ values }) => (
      <>
        <div className="form-group">
          <label>Active</label>
          <FormCheckGroup name="active" type="checkbox" label="" asSwitch />
        </div>
        <div className="row">
          <div className="col">
            <FormGroup name="name.full.en" label="Name (English)" />
          </div>
          <div className="col">
            <FormGroup name="title" />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <FormGroup name="username" autoComplete="new-password" />
          </div>
          <div className="col">
            <FormGroup name="userId" label="Oracle User ID" />
          </div>
          <div className="col">
            <FormGroup name="salesRepId" label="Sales Rep ID" />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <FormGroup
              name="orgIds"
              label="Organization ID (Comma separated)"
              disabled={notAdmin}
              validate={(value) => {
                if (notAdmin) return;
                if (value && values?.showroom) return 'Please select either Showroom or Organization ID';
              }}
            />
          </div>
          <div className="col-3">
            <FormGroup
              name="showroom"
              as="select"
              disabled={notAdmin}
              validate={(value) => {
                if (notAdmin) return;
                if (value && values?.orgIds) return 'Please select either Showroom or Organization ID';
              }}>
              <option value="">-</option>
              {showrooms.map((v) => (
                <option value={v._id} key={v._id}>
                  {`${v.orgId} - ${v.showroom}`}
                </option>
              ))}
            </FormGroup>
          </div>
          <div className="col-3">
            <FormGroup name="department" label="Roles" as="select" disabled={notAdmin}>
              <option value="">-</option>
              {departments.map((v) => (
                <option value={v._id} key={v._id}>
                  {`${v.name}`}
                </option>
              ))}
            </FormGroup>
          </div>
        </div>
        <div className="row">
          <div className="col-6">
            <FormGroup name="email" />
          </div>
          <div className="col-2">
            <FormGroup name="mobile.code" />
          </div>
          <div className="col-4">
            <FormGroup name="mobile.number" />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <FormGroup
              name="password"
              autoComplete="new-password"
              type="password"
              placeholder="Leave this blank to keep the stored password"
            />
          </div>
          <div className="col">
            <FormGroup
              name="confirmPassword"
              type="password"
              placeholder="Leave this blank to keep the stored password"
            />
          </div>
        </div>
        <FormGroup name="salesGroupIds" label="Sales Group IDs (Comma separated)" />
        <FormGroup name="lastLogin" disabled />
      </>
    )}
  </FormResource>
);

export default FormDepartment;
